import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlSerializer,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, filter, map, take, tap } from 'rxjs/operators';

import { GroupsBackend } from '@libs/src/groups/groups.backend';

import { GetGroupSuccess } from '@libs/src/groups/groups-store/groups.actions';
import { GroupsState } from '@libs/src/groups/groups-store/groups.interfaces';
import { Account } from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { getMembership } from '@main-client/src/app/account/account.helpers';
import { IAppState } from '@main-client/src/app/app.state';
import { SetCurrentGroup } from '@main-client/src/app/current-group-store/current-group.actions';

import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';

@Injectable()
export class GroupLoadedGuard implements CanActivate {
  constructor(
    private readonly groupsBackend: GroupsBackend,
    private readonly router: Router,
    private readonly serializer: UrlSerializer,
    private readonly store: Store<IAppState>,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const groupSlug = route.paramMap.get('groupSlug');
    return this.store.select('groups').pipe(
      take(1),
      map((state: GroupsState[]) =>
        find(state, { document: { slug: groupSlug } }),
      ),
      concatMap((state: GroupsState) => {
        const getGroup = this.makeGetGroup(groupSlug);
        return state && state.isLoaded ? of(state.document) : getGroup;
      }),
      tap((group: Group) => this.store.dispatch(SetCurrentGroup(group._id))),
      concatMap((group: Group) => {
        return this.store.select('user').pipe(
          filter((user: Account) => !isEmpty(user)),
          take(1),
          map((user: Account) => {
            const membership = getMembership(user.groups, group._id);
            const isMember = !isEmpty(membership);
            const canAccessHub = user.isAdmin || isMember;
            return (
              canAccessHub || this.navigateToHubJoinView(state.url, groupSlug)
            );
          }),
        );
      }),
      catchError(() => {
        const notFoundUrl = this.router.createUrlTree(['/not-found'], {
          queryParams: {
            page: this.router.url,
          },
        });
        return of(notFoundUrl);
      }),
    );
  }

  navigateToHubJoinView(
    url: RouterStateSnapshot['url'],
    groupSlug: Group['slug'],
  ) {
    return this.router.createUrlTree(['/hubs'], {
      queryParams: {
        groupSlug,
        page: this.router.url,
        ...this.getQueryParamsFromUrl(url),
      },
    });
  }

  getQueryParamsFromUrl(urlString: string): {
    [key: string]: string[] | string;
  } {
    const urlTree = this.serializer.parse(urlString);
    const urlSegments =
      urlTree?.root?.children?.primary?.segments?.map(
        (urlSegment: UrlSegment) => urlSegment.path,
      ) || [];
    return {
      ...urlTree.queryParams,
      redirectPathAfterJoining: urlSegments,
    };
  }

  private makeGetGroup(groupSlug: string) {
    return this.groupsBackend.getGroup({ slug: groupSlug }).pipe(
      catchError((response: HttpErrorResponse) => throwError(response)),
      tap((response: Group) => {
        this.store.dispatch(GetGroupSuccess({ group: response }));
      }),
      map((response: Group) => response),
    );
  }
}
